import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';

import { openModal as openModalAction, closeModal } from '../../actions/modalActions';

import { Panel } from '../Panel';
import { MapTCC } from '../Map';
import Selectors from '../Selectors/Selectors';
import { Legend } from '../Legend';
import { Modal } from '../Modal';
import SplitViewButton from '../../components/View/SplitViewButton';
import CloseSplitViewButton from '../../components/View/CloseSplitViewButton';
import { splitViewEnabledSelector } from '../../selectors/splitViewSelectors';
import { trackEvent } from '../../actions/analyticsActions';
import { closeSplitView, switchView } from '../../utils/url';
import styles from './View.css';
import { FieldFeature, NamedEntity } from '../../types';
import { AppState } from '../../store/types/Store';

interface OwnProps {
  index: number;
  panel?: React.ReactElement;
  mapLayer?: React.ReactElement;
  legend?: React.ReactElement;
  content?: React.ReactElement;
  modal?: React.ReactElement;
}

interface StateProps {
  activeMapLayers: NamedEntity[];
  activeSeasons: NamedEntity[];
  appInitializerHasFetched: boolean;
  fieldId: string;
  isSplitViewEnabled: boolean;
  isSplitViewButtonVisible: boolean;
  layer: string;
  season: string;
  userHasFetched: boolean;
  hasSplitViewAsset: boolean;
}

interface DispatchProps {
  trackAnalyticsEvent: (name: string, action: string) => void;
  openModal: typeof openModalAction;
  closeModal: typeof closeModal;
}

type ViewProps = OwnProps & StateProps & DispatchProps;

interface ViewState {
  showMap: boolean;
}

export class View extends Component<ViewProps, ViewState> {
  constructor(props: ViewProps) {
    super(props);
    this.state = {
      showMap: true,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: ViewProps) {
    const { showMap } = this.state;
    const { appInitializerHasFetched } = this.props;

    if (
      !showMap &&
      !appInitializerHasFetched &&
      nextProps.appInitializerHasFetched
    ) {
      this.setState({ showMap: true });
    }
    if (
      showMap &&
      appInitializerHasFetched &&
      !nextProps.appInitializerHasFetched
    ) {
      this.setState({ showMap: false });
    }
  }

  splitView = () => {
    const {
      activeSeasons,
      activeMapLayers,
      season,
      layer,
      fieldId,
      trackAnalyticsEvent,
    } = this.props;

    const seasonIds = activeSeasons.map((activeSeason) => activeSeason.id);
    const layerIds = activeMapLayers.map((activeLayer) => activeLayer.id);

    switchView(1, season, seasonIds, layer, layerIds, fieldId);

    if (trackAnalyticsEvent) {
      trackAnalyticsEvent('split_view', 'selected');
    }

  };

  closeSplitView = () => {
    const { season, layer, fieldId, trackAnalyticsEvent } = this.props;

    closeSplitView(season, layer, fieldId);

    if (trackAnalyticsEvent) {
      trackAnalyticsEvent('split_view', 'closed');
    }
  };

  render() {
    const {
      appInitializerHasFetched,
      index,
      panel,
      mapLayer,
      legend,
      userHasFetched,
      content,
      modal,
      isSplitViewButtonVisible,
      isSplitViewEnabled,
    } = this.props;
    const hasFetched = appInitializerHasFetched && userHasFetched;

    const isPanelReady = userHasFetched;

    /* eslint-disable no-underscore-dangle */
    const _legend =
      legend &&
      React.cloneElement(legend, {
        index,
        testId: 'dmb-legend',
      });
    const _panel = isPanelReady && panel ? panel : undefined;
    const _mapLayer =
      hasFetched && mapLayer
        ? React.cloneElement(mapLayer, { index })
        : undefined;

    const _modal = modal ? React.cloneElement(modal) : undefined;
    /* eslint-enable no-underscore-dangle */
    const { showMap } = this.state;

    return (
      <div className={styles.view} data-test-id="dmb-view">
        <Panel index={index} panel={_panel} />
        <Modal index={index} modal={_modal} />
        {content || (
          <MapTCC index={index} mapLayer={_mapLayer} showMap={showMap} />
        )}
        {hasFetched && !content && <Selectors index={index} />}
        {hasFetched && legend && !content && <Legend legendItem={_legend} />}
        {hasFetched && !content && (
          <SplitViewButton
            onClick={this.splitView}
            show={isSplitViewButtonVisible}
          />
        )}
        {!content && (
          <CloseSplitViewButton
            onClick={this.closeSplitView}
            show={isSplitViewEnabled && index === 0}
          />
        )}
      </div>
    );
  }
}

function mapStateToProps(state: AppState, props: OwnProps): StateProps {
  const activeMapLayer = state.layers.activeMapLayers[props.index];
  const activeSeason = state.seasons.activeSeasons[props.index];

  return {
    activeMapLayers: state.layers.activeMapLayers,
    activeSeasons: state.seasons.activeSeasons,
    appInitializerHasFetched: state.appInitializer?.hasFetched ?? false,
    fieldId: state.field.id ?? '',
    isSplitViewEnabled: state.layers.isSplitViewEnabled,
    isSplitViewButtonVisible: splitViewEnabledSelector(state),
    layer: activeMapLayer?.id ?? '',
    season: activeSeason?.id ?? '',
    userHasFetched: state.user.hasFetched,
    hasSplitViewAsset: state.field?.features?.includes(FieldFeature.SPLIT_VIEW) ?? false,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return bindActionCreators(
    {
      trackAnalyticsEvent: trackEvent,
      openModal: openModalAction,
      closeModal,
    },
    dispatch
  ) as unknown as DispatchProps;
}

export default connect(mapStateToProps, mapDispatchToProps)(View);
